import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Container } from "@mui/material";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { 
  useLazyGetUserQuery, 
  useGetUserTrackersQuery,
  useGetCostCenterUsersQuery 
} from "state/rtkQuery/user/userQueries";
import { useDebounce } from "utils/useDebounce";

import Pagination from "pages/homeDashboard/components/Pagination/Pagination";
import UserDetailsModal from "./components/UserDetailsModal/UserDetailsModal";
import CountryFilterDropdown from "components/shared/CountryFilterDropdown/CountryFilterDropdown";
import {
  InactiveUsersHeader,
  InactiveUsersTable,
  SelectedCountriesDisplay,
  UserSearchInput,
} from "./components/InactiveUsers";

import "./InactiveUsers.css";


const InactiveUsers = () => {
  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [aggregatedCountries, setAggregatedCountries] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 500);
  
  // User details modal state
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [isUserDetailsLoading, setIsUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState("");

  const recordsPerPage = 20;
  const statusFilter = "inactive"; // Always show inactive users

  // Get user's permissions from Redux store
  const userPermissions = useSelector((state) => state.auth.permissions);

  // Get all active organizations
  const { data: allOrgs, isSuccess: isOrgsSuccess } = useGetActiveOrganizationsQuery();
  
  // Get platform data
  const { data: platformData } = useGetPlatformsQuery();
  
  // Lazy query for user details
  const [triggerGetUser] = useLazyGetUserQuery();
  
  // Get cost center data
  const {
    data: costCenterListData,
    isSuccess: isCostCenterListDataSuccess,
  } = useGetCostCenterUsersQuery(
    {
      platformId: platformData?._id,
    },
    { skip: !platformData?._id }
  );

  // Create organization ID to name mapping
  const orgIdToName = useMemo(() => {
    if (!isOrgsSuccess || !Array.isArray(allOrgs)) {
      return {};
    }

    return allOrgs.reduce((acc, org) => {
      acc[org._id] = org.name;
      return acc;
    }, {});
  }, [allOrgs, isOrgsSuccess]);

  // Create cost center ID to name mapping
  const costCenterById = useMemo(() => {
    const costCenters = costCenterListData?.cost_centers;

    if (!isCostCenterListDataSuccess || !Array.isArray(costCenters)) {
      return {};
    }

    return costCenters.reduce((acc, costCenter) => {
      acc[costCenter._id] = costCenter.name;
      return acc;
    }, {});
  }, [costCenterListData, isCostCenterListDataSuccess]);

  // Aggregate countries from all organizations where user is Owner or Approver
  useEffect(() => {
    if (!userPermissions || !allOrgs) return;

    // Filter to get orgIds where user is Owner or Approver
    const adminOrgIds = userPermissions
      .filter((perm) => perm.userType === "Owner" || perm.userType === "Approver")
      .map((perm) => perm.orgId);

    // Filter allOrgs to only include admin orgs and extract countries
    const adminOrgs = allOrgs.filter((org) => adminOrgIds.includes(org._id));

    // Aggregate all countries from admin orgs
    const allCountries = adminOrgs.reduce((acc, org) => {
      if (org.countries && Array.isArray(org.countries)) {
        return [...acc, ...org.countries];
      }
      return acc;
    }, []);

    // Deduplicate and sort countries
    const uniqueCountries = [...new Set(allCountries)].sort();

    setAggregatedCountries(uniqueCountries);

    // Set all countries as selected by default
    if (uniqueCountries.length > 0 && selectedCountries.length === 0) {
      setSelectedCountries(uniqueCountries);
    }
  }, [userPermissions, allOrgs, selectedCountries.length]);

  // Fetch user trackers data with selected countries (comma-separated) and search
  const {
    data: userTrackersData,
    isLoading: isUserTrackersLoading,
    isFetching: isFetchingUserTrackers,
  } = useGetUserTrackersQuery(
    {
      page: currentPage,
      perPage: recordsPerPage,
      country: selectedCountries.join(','),
      status: statusFilter,
      searchParams: debouncedSearch,
    },
    {
      skip: selectedCountries.length === 0,
      refetchOnMountOrArgChange: true,
    }
  );

  // Use data directly from API (no client-side filtering needed)
  const filteredUsers = useMemo(() => {
    return userTrackersData?.userTrackers || [];
  }, [userTrackersData]);

  // Get total count from API response
  const totalCount = userTrackersData?.total || 0;

  // Handle opening user details modal
  const handleOpenUserDetails = async (row) => {
    setShowUserDetailsModal(true);
    setIsUserDetailsLoading(true);
    setUserDetailsError("");

    try {
      const result = await triggerGetUser({
        email: row.email,
        platformId: platformData?._id,
      });
      const users = Array.isArray(result?.data) ? result.data : [];
      const exactMatch = users.find((user) => user._id === row.id);

      setSelectedUserDetails(exactMatch || users[0] || null);

      if (!users.length) {
        setUserDetailsError("Could not find user details for this user.");
      }
    } catch (error) {
      setSelectedUserDetails(null);
      setUserDetailsError("Failed to load user details. Please try again.");
    } finally {
      setIsUserDetailsLoading(false);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCountries, debouncedSearch]);

  const nPages = Math.ceil(totalCount / recordsPerPage);

  return (
    <Container
      sx={{
        maxWidth: "1300px !important",
        flex: "1",
        marginTop: "3rem",
        marginBottom: "3rem",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "32px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        {/* Header Section */}
        <InactiveUsersHeader totalCount={totalCount} />

        {/* Countries Display and Filters Row */}
        <Box 
          sx={{ 
            marginTop: "20px",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: "20px",
            flexWrap: "wrap"
          }}
        >
          {/* Countries Display */}
          <SelectedCountriesDisplay
            selectedCountries={selectedCountries}
            totalCountries={aggregatedCountries.length}
          />

          {/* Search and Country Selector */}
          <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "flex-end" }}>
            {/* Search Input */}
            <UserSearchInput
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
              
            {/* Country Filter Dropdown */}
            <CountryFilterDropdown
              countries={aggregatedCountries}
              selectedCountries={selectedCountries}
              onApply={setSelectedCountries}
            />
          </Box>
        </Box>

        {/* Data Grid */}
        <InactiveUsersTable
          users={filteredUsers}
          showCountryColumn={aggregatedCountries.length > 1}
          isLoading={isUserTrackersLoading || isFetchingUserTrackers}
          onUserClick={handleOpenUserDetails}
        />

        {/* Pagination Section */}
        {nPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: "24px",
            }}
          >
            <Pagination
              nPages={nPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </Box>
        )}
      </Box>

      {/* User Details Modal */}
      <UserDetailsModal
        open={showUserDetailsModal}
        onClose={() => {
          setShowUserDetailsModal(false);
          setSelectedUserDetails(null);
          setUserDetailsError("");
        }}
        user={selectedUserDetails}
        platformId={platformData?._id}
        orgIdToName={orgIdToName}
        costCenterById={costCenterById}
        isLoading={isUserDetailsLoading}
        error={userDetailsError}
      />
    </Container>
  );
};

export default InactiveUsers;
